// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API module for locking an initial true-up close run.
 * Provides type-safe initial true-up locking with Zod validation.
 */
import * as z from 'zod';
import { initialTrueUpCloseRunResponseSchema } from '@/accounting/api/types';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';

/**
 * Schema for validating the response from the lock initial true-up API endpoint.
 * Includes both the initial true-up close run data and metadata about the request.
 */
const lockInitialTrueUpResponseSchema = z
  .object({
    data: initialTrueUpCloseRunResponseSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    initialTrueUpCloseRun: data,
  }));

/**
 * Type definition for the lock initial true-up API response.
 * Contains the locked initial true-up close run data and request metadata.
 */
type LockInitialTrueUpResponse = z.infer<
  typeof lockInitialTrueUpResponseSchema
>;

/**
 * Request body for locking an initial true-up close run.
 */
interface LockInitialTrueUpRequest {
  signature: string;
  notes?: string;
}

/**
 * Locks an initial true-up close run, preventing further modifications.
 *
 * @param id - The unique identifier of the initial true-up close run to lock
 * @param signature - The user's signature confirming the lock
 * @param notes - Optional notes about the lock
 * @returns Promise resolving to the locked initial true-up close run data and request metadata
 * @throws Will throw an error if the close run is not found, already locked,
 *         API request fails, or response validation fails
 */
export async function lockInitialTrueUp(
  id: string,
  signature: string,
  notes?: string
): Promise<LockInitialTrueUpResponse> {
  const url = `/v1/accounting/initial-true-ups/${id}/lock`;
  const requestBody: LockInitialTrueUpRequest = { signature };
  if (notes) {
    requestBody.notes = notes;
  }
  const response = await api.post(url, requestBody);
  return parseResponse(lockInitialTrueUpResponseSchema, response.data, {
    schemaName: 'LockInitialTrueUp',
    endpoint: url,
  });
}
